import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, ApiError, type AcquisitionRow, type LicenceRow } from '../../lib/api'

/**
 * The seat, from the reader's side.
 *
 * Enforcement is entirely server-side (`services/content_session.py`); this
 * hook only asks. `POST /content-sessions/{licence}/heartbeat` is the one call
 * that does everything: it resumes a live session, claims a seat that has come
 * free, or re-reports the queue position. Polling it is therefore both the
 * keep-alive and the way a queued reader is given their seat without
 * reloading, which is what lets the queue screen say "this page will open by
 * itself".
 *
 * Every figure the queue screen prints comes back from that call. Two do not
 * exist in the API yet and are therefore never invented here: the claim window
 * (`SESSION_QUEUE_CLAIM_WINDOW_S`, server-side only) and today's peak and
 * denial counts (admin-only). Where a figure is absent the card says so.
 */

/** How often the queue screen asks. The board's copy promises five seconds. */
export const SEAT_POLL_MS = 5000

/**
 * The acquisition. `claim_window_s` is the licence's claim window, so the offer
 * counts down against the real deadline rather than a guess; `queue_depth` now
 * comes back on an active acquisition too, which is what lets a reader holding
 * the seat be told honestly that somebody is waiting for it. A denial carries
 * `ended_at`, `ended_by` and `note` when library staff ended the session.
 */
export interface SeatAcquisition extends AcquisitionRow {
  claim_window_s?: number | null
  ended_at?: string | null
  ended_by?: string | null
  note?: string | null
}

/** One live session of the caller's own, from `GET /content-sessions/mine`. */
export interface MySeatSession {
  licence_id: string
  session_id: string
  publication_name: string | null
  started_at: string
  last_seen_at: string
  expires_at: string
  idle_timeout_s: number | null
  max_s: number | null
  queue_depth: number
}

/** The caller's own place in a queue, where they are in one. */
export interface MyQueueEntry {
  licence_id: string
  publication_name?: string | null
  position: number | null
  depth: number | null
  estimated_wait_s: number | null
  requested_at?: string | null
  status?: string | null
}

/** How the caller's last session ended, and who ended it. */
export interface MyLastEnded {
  licence_id: string
  reason: string | null
  ended_at: string | null
  ended_by: string | null
  note: string | null
}

export interface MySeatState {
  sessions: MySeatSession[]
  queue: MyQueueEntry[]
  last_ended: MyLastEnded | null
}

export interface SeatState {
  /** The last answer from the server, or null before the first one lands. */
  acquisition: SeatAcquisition | null
  /** When this browser first saw itself queued, for the "queued at" record. */
  queuedAt: Date | null
  /** Seconds this screen has been asking, so the copy can say how long. */
  elapsedS: number
  /**
   * True for one transition: the poll that was queued last time and is active
   * now. The server claims the offer during that same call, so this is the
   * moment the seat was handed over rather than a separate offered status.
   */
  justOffered: boolean
  error: unknown
  refresh: () => void
  leaveQueue: () => Promise<void>
  release: () => Promise<void>
  dismissOffer: () => void
}

export function useSeat(licenceId: string | null, enabled = true): SeatState {
  const [acquisition, setAcquisition] = useState<SeatAcquisition | null>(null)
  const [queuedAt, setQueuedAt] = useState<Date | null>(null)
  const [elapsedS, setElapsedS] = useState(0)
  const [justOffered, setJustOffered] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [nonce, setNonce] = useState(0)
  const previous = useRef<AcquisitionRow['status'] | null>(null)

  const on = enabled && !!licenceId

  useEffect(() => {
    if (!on) return
    let cancelled = false

    const ask = async () => {
      try {
        const next = await api.post<SeatAcquisition>(
          `/content-sessions/${licenceId}/heartbeat`,
        )
        if (cancelled) return
        setError(null)
        setAcquisition(next)
        if (next.status === 'queued') {
          setQueuedAt((current) => current ?? new Date())
        }
        if (previous.current === 'queued' && next.status === 'active') {
          setJustOffered(true)
        }
        previous.current = next.status
      } catch (caught) {
        if (!cancelled) setError(caught)
      }
    }

    void ask()
    const poll = setInterval(() => void ask(), SEAT_POLL_MS)
    const tick = setInterval(() => setElapsedS((value) => value + 1), 1000)
    return () => {
      cancelled = true
      clearInterval(poll)
      clearInterval(tick)
    }
  }, [licenceId, on, nonce])

  const refresh = useCallback(() => setNonce((value) => value + 1), [])

  const leaveQueue = useCallback(async () => {
    if (!licenceId) return
    await api.del<void>(`/content-sessions/${licenceId}/queue`)
    setAcquisition(null)
    setQueuedAt(null)
    previous.current = null
  }, [licenceId])

  const release = useCallback(async () => {
    if (!licenceId) return
    await api.post<void>(`/content-sessions/${licenceId}/release`)
    setAcquisition(null)
    previous.current = null
  }, [licenceId])

  const dismissOffer = useCallback(() => setJustOffered(false), [])

  return {
    acquisition,
    queuedAt,
    elapsedS,
    justOffered,
    error,
    refresh,
    leaveQueue,
    release,
    dismissOffer,
  }
}

/**
 * The caller's own seat state, read-only.
 *
 * `GET /content-sessions/mine` reports without acquiring, which is the whole
 * point of it: asking the acquire endpoint what the seat is doing would take
 * the seat to answer, and on a one-seat licence that is exactly the wrong
 * thing for a status screen to do.
 */
export function useMySeatState(enabled = true, pollMs = SEAT_POLL_MS) {
  const [state, setState] = useState<MySeatState | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    const ask = async () => {
      try {
        const next = await api.get<MySeatState>('/content-sessions/mine')
        if (!cancelled) {
          setState(next)
          setError(null)
        }
      } catch (caught) {
        if (!cancelled) setError(caught)
      }
    }
    void ask()
    const poll = pollMs ? setInterval(() => void ask(), pollMs) : null
    return () => {
      cancelled = true
      if (poll) clearInterval(poll)
    }
  }, [enabled, pollMs, nonce])

  return { state, error, refresh: () => setNonce((value) => value + 1) }
}

/** Seconds left on a session, from the server's own `expires_at`. */
export function useExpiryCountdown(expiresAt: string | null | undefined) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(tick)
  }, [])
  return useMemo(() => {
    if (!expiresAt) return null
    return Math.max(0, Math.round((new Date(expiresAt).getTime() - now) / 1000))
  }, [expiresAt, now])
}

/**
 * The licence this reader's institution holds for the publication they are
 * looking at. The queue screen needs it for the licence line ("concurrent ·
 * limit 1 · Lakehead") and the denial screen for the date scope.
 */
export function useMyLicences(enabled = true) {
  const [licences, setLicences] = useState<LicenceRow[] | null>(null)
  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    api
      .get<LicenceRow[]>('/licences/mine')
      .then((rows) => {
        if (!cancelled) setLicences(rows)
      })
      .catch(() => {
        // A signed-out reader has no licences; that is a state, not an error.
        if (!cancelled) setLicences([])
      })
    return () => {
      cancelled = true
    }
  }, [enabled])
  return licences
}

/** Find one licence by id among the caller's own. */
export function licenceById(licences: LicenceRow[] | null, id: string | null | undefined) {
  if (!licences || !id) return null
  return licences.find((row) => row.id === id) ?? null
}

/**
 * A session of one's own, about to expire.
 *
 * The idle timeout is licence configuration, so the countdown is real; what
 * the API cannot yet tell an active reader is whether anyone is waiting behind
 * them, so the card only claims that when `queue_depth` comes back set.
 */
export function useSessionCountdown(idleTimeoutS: number | null, activeSince: number | null) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(tick)
  }, [])
  return useMemo(() => {
    if (!idleTimeoutS || !activeSince) return null
    const remaining = Math.max(0, idleTimeoutS - Math.floor((now - activeSince) / 1000))
    return remaining
  }, [idleTimeoutS, activeSince, now])
}

/** m:ss, the way both countdown cards on board 06 print one. */
export function clock(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds))
  const minutes = Math.floor(whole / 60)
  return `${minutes}:${String(whole % 60).padStart(2, '0')}`
}

/** "About 6 minutes", from the server's estimate in seconds. */
export function waitPhrase(seconds: number | null | undefined): string | null {
  if (seconds === null || seconds === undefined) return null
  if (seconds < 90) return 'under two minutes'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `about ${minutes} minute${minutes === 1 ? '' : 's'}`
  const hours = Math.round(seconds / 3600)
  return `about ${hours} hour${hours === 1 ? '' : 's'}`
}

const ORDINALS = [
  'first',
  'second',
  'third',
  'fourth',
  'fifth',
  'sixth',
  'seventh',
  'eighth',
  'ninth',
  'tenth',
]

/** "You are second in the queue." The board spells the position out. */
export function ordinal(position: number): string {
  return ORDINALS[position - 1] ?? `number ${position}`
}

/** The status code of an error, when it has one. */
export function statusOf(error: unknown): number | null {
  return error instanceof ApiError ? error.status : null
}
